/**
 * Output section for the Test Suite Configuration page.
 *
 * Provides YAML preview, download, and save-to-S3 functionality within
 * an expandable "Generated Config" section. Displays the generated YAML
 * in a read-only code block with syntax highlighting, the suggested
 * filename, and action buttons for download and S3 persistence.
 *
 * Error handling covers 422 (missing sections) and 500 (save failure)
 * with retry options. A success alert confirms the saved S3 path.
 *
 * @see Requirements 11.1–11.4, 12.1–12.3, 13.1–13.5
 */

import React, { useState, useEffect } from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  Button,
  Input,
  Alert,
  FormField,
  StatusIndicator,
  Box,
  Header,
  Container,
} from '@amzn/awsui-components-react';
import {
  ConfigGeneratorApiError,
  ConfigPageAction,
  SaveConfigResponse,
} from './types';

// ─── Props ─────────────────────────────────────────────────────────────

export interface OutputSectionProps {
  yamlContent: string;
  filename: string;
  toolName: string;
  s3TargetPath: string;
  onPreviewYaml: () => Promise<void>;
  onDownload: () => void;
  onSaveToS3: (targetPath: string) => Promise<void>;
  isGenerating: boolean;
  isSaving: boolean;
  saveResult: SaveConfigResponse | null;
  saveError: ConfigGeneratorApiError | null;
  generateError: ConfigGeneratorApiError | null;
  dispatch: React.Dispatch<ConfigPageAction>;
}

// ─── Constants ─────────────────────────────────────────────────────────

const MISSING_SECTIONS_STATUS = 422;
const SAVE_FAILURE_STATUS = 500;
const DEFAULT_CONFIG_FILENAME = 'generator_config_v3.yaml';
const S3_PATH_PREFIX = 'configs';

/** Styles for the read-only YAML code preview block. */
const CODE_PREVIEW_STYLES: React.CSSProperties = {
  backgroundColor: '#1b1f27',
  color: '#d4d4d4',
  padding: '16px',
  borderRadius: '8px',
  overflow: 'auto',
  maxHeight: '500px',
  fontSize: '13px',
  lineHeight: '1.5',
  fontFamily: '"Monaco", "Menlo", "Consolas", monospace',
  whiteSpace: 'pre',
  margin: 0,
};

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Generates a default S3 path based on the tool name.
 * Sanitizes the tool name to produce a safe path segment.
 */
function getDefaultS3Path(toolName: string): string {
  if (!toolName) return '';
  const sanitized = toolName.toLowerCase().replace(/[^a-z0-9_-]/g, '_');
  return `${S3_PATH_PREFIX}/${sanitized}/${DEFAULT_CONFIG_FILENAME}`;
}

// ─── Component ─────────────────────────────────────────────────────────

const OutputSection: React.FC<OutputSectionProps> = ({
  yamlContent,
  filename,
  toolName,
  s3TargetPath,
  onPreviewYaml,
  onDownload,
  onSaveToS3,
  isGenerating,
  isSaving,
  saveResult,
  saveError,
  generateError,
  dispatch,
}) => {
  // Local state for the editable S3 target path input
  const [localS3Path, setLocalS3Path] = useState(
    s3TargetPath || getDefaultS3Path(toolName),
  );

  // Sync local path when props change (e.g., tool switch resets s3TargetPath)
  useEffect(() => {
    setLocalS3Path(s3TargetPath || getDefaultS3Path(toolName));
  }, [s3TargetPath, toolName]);

  const hasYaml = yamlContent.length > 0;

  const handleS3PathChange = (value: string) => {
    setLocalS3Path(value);
    dispatch({ type: 'SET_S3_TARGET_PATH', path: value });
  };

  const handleSave = async () => {
    await onSaveToS3(localS3Path);
  };

  // Derive error type for display
  const isMissingSections = generateError?.statusCode === MISSING_SECTIONS_STATUS;
  const isSaveFailure = saveError?.statusCode === SAVE_FAILURE_STATUS;

  return (
    <ExpandableSection
      headerText="Generated Config"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="l">
        {/* ── Preview YAML Button ──────────────────────────────────── */}
        <Box>
          <SpaceBetween size="s" direction="horizontal" alignItems="center">
            <Button
              variant="primary"
              onClick={onPreviewYaml}
              loading={isGenerating}
              disabled={isGenerating}
              ariaLabel="Preview YAML"
            >
              Preview YAML
            </Button>
            {isGenerating && (
              <StatusIndicator type="loading">
                Generating YAML...
              </StatusIndicator>
            )}
          </SpaceBetween>
        </Box>

        {/* ── Generate Error Alert ─────────────────────────────────── */}
        {generateError && (
          <Alert
            type="error"
            header={
              isMissingSections
                ? 'Missing required sections'
                : 'YAML generation failed'
            }
            action={
              <Button onClick={onPreviewYaml} disabled={isGenerating}>
                Retry
              </Button>
            }
          >
            {isMissingSections
              ? `Some required config sections are incomplete: ${generateError.detail}`
              : generateError.detail || 'An unexpected error occurred during YAML generation.'}
          </Alert>
        )}

        {/* ── YAML Preview ─────────────────────────────────────────── */}
        {hasYaml && (
          <Container
            header={
              <Header
                variant="h3"
                description={filename ? `Suggested filename: ${filename}` : undefined}
              >
                YAML Preview
              </Header>
            }
          >
            <pre
              style={CODE_PREVIEW_STYLES}
              aria-label="Generated YAML content"
            >
              {yamlContent}
            </pre>
          </Container>
        )}

        {/* ── Download & Save Actions ──────────────────────────────── */}
        {hasYaml && (
          <SpaceBetween size="l">
            {/* Download button */}
            <Button
              iconName="download"
              onClick={onDownload}
              ariaLabel="Download YAML file"
            >
              Download
            </Button>

            {/* Save to S3 */}
            <Container header={<Header variant="h3">Save to S3</Header>}>
              <SpaceBetween size="m">
                <FormField
                  label="S3 target path"
                  description="The S3 path where the config file will be saved"
                >
                  <Input
                    value={localS3Path}
                    onChange={({ detail }) => handleS3PathChange(detail.value)}
                    placeholder="s3://bucket/path/to/config.yaml"
                    disabled={isSaving}
                    ariaLabel="S3 target path"
                  />
                </FormField>

                <Box>
                  <SpaceBetween size="s" direction="horizontal" alignItems="center">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      loading={isSaving}
                      disabled={isSaving || localS3Path.trim().length === 0}
                      ariaLabel="Save to S3"
                    >
                      Save to S3
                    </Button>
                    {isSaving && (
                      <StatusIndicator type="loading">
                        Saving to S3...
                      </StatusIndicator>
                    )}
                  </SpaceBetween>
                </Box>

                {/* Save Error Alert */}
                {saveError && (
                  <Alert
                    type="error"
                    header={
                      isSaveFailure
                        ? 'Save failed'
                        : 'Error saving to S3'
                    }
                    action={
                      <Button onClick={handleSave} disabled={isSaving}>
                        Retry
                      </Button>
                    }
                  >
                    {isSaveFailure
                      ? 'The config file could not be saved to S3. Please check the target path and try again.'
                      : saveError.detail || 'An unexpected error occurred while saving.'}
                  </Alert>
                )}

                {/* Save Success Alert */}
                {saveResult && (
                  <Alert type="success" header="Config saved successfully">
                    {saveResult.message || `Config saved to: ${saveResult.saved_path}`}
                  </Alert>
                )}
              </SpaceBetween>
            </Container>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default OutputSection;
